import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar/Sidebar';
import { LanguagesBar } from '../../components/LanguagesBar';
import { AboutMeProps } from './types';
import { AppState } from '../../store/rootReducer';
import { getLanguagesList } from '../../store/languages/selectors';
import { connect, useDispatch } from 'react-redux';
import { getUserId } from '../../store/auth/selectors';
import { Box, Button, Grid, TextField, TextareaAutosize } from '@mui/material';
import AddImage from '../../components/AddImage/AddImage';
import { storage } from '../../resources/storage';
import strings from '../../resources/strings';
import { autoId } from '../../utils/UidGenerator';
import SaveIcon from '@mui/icons-material/Save';
import './AboutmePage.scss';
import { validateLanguages, validatePhoneNumber } from '../../utils/Validator';
import SnackbarMessage from '../../components/SnackbarMessage/SnackbarMessage';
import { clearCoachError, requestAddCoachInfo, requestGetListCoach } from '../../store/coach/actions';
import { getCoachList, getError } from '../../store/coach/selectors';

const MAX_DESCRIPTION_LENGTH = 300;
const AboutMePage: React.FC<AboutMeProps> = (props) => {
    const [languageValue, setLanguageValue] = useState<string>('pl');
    const [description, setDescription] = useState<Record<string, string>>({});
    const [image, setImage] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');
    const dispatch = useDispatch();
    const uniqueId = autoId();

    useEffect(() => {
        dispatch(requestGetListCoach());
    }, []);

    useEffect(() => {
        const currentCoach = props.coach.find((x) => x.uid === props.userId);
        setDescription(currentCoach?.description ?? {});
        setImage(currentCoach?.image ?? '');
        setPhoneNumber(currentCoach?.phoneNumber ?? '');
    }, [props.coach]);

    const handleSubmit = (description: Record<string, string>, image: string, phoneNumber: string, uid: string) => {
        if (!validateLanguages([{ description: description }], props.languages.length)) {
            setError(strings.languagesValidation.errorMessage);
            return;
        }
        if (!validatePhoneNumber(phoneNumber)) {
            setError(strings.coachPage.errors.errorPhoneNumber);
            return;
        }
        dispatch(requestAddCoachInfo(description, image, phoneNumber, uid));
        setSuccessMessage(strings.coachPage.successMessage);
    };

    return (
        <div className="content">
            <Sidebar></Sidebar>
            <div className="main">
                <LanguagesBar
                    languages={props.languages}
                    selectedLanguage={languageValue}
                    handleChange={setLanguageValue}
                ></LanguagesBar>
                <Grid container>
                    <Grid item xs={6} className="about-section">
                        <h6 className="main-text">{`Description (maximum ${MAX_DESCRIPTION_LENGTH} characters)`}</h6>
                        {props.languages.map((lang) => (
                            <TextareaAutosize
                                key={`description_${lang.code}`}
                                defaultValue={description?.[lang.code]}
                                id={`description_${lang.code}`}
                                name={`description_${lang.code}`}
                                placeholder="Description..."
                                maxRows={6}
                                maxLength={MAX_DESCRIPTION_LENGTH}
                                onInput={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                    setDescription({ ...description, [lang.code]: e.target.value });
                                }}
                                style={{
                                    display: lang.code === languageValue ? 'block' : 'none',
                                    marginBottom: '2rem',
                                    marginTop: '0.5rem',
                                    fontSize: '1.1rem',
                                    fontFamily: 'inherit',
                                }}
                            ></TextareaAutosize>
                        ))}
                        <TextField
                            placeholder={'Phone number'}
                            value={phoneNumber}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                setPhoneNumber(e.target.value);
                            }}
                            fullWidth
                            label={'Phone number'}
                        ></TextField>
                        <h3 className="main-title">{strings.newsAndEvents.image}</h3>
                        <AddImage
                            path={`${storage.coach}`}
                            actualEventFile={image}
                            setGetImgAddress={(url: string) => setImage(url)}
                            customName={uniqueId}
                            sendOnDrop
                        ></AddImage>
                        <Box>
                            <SnackbarMessage
                                serverity="error"
                                message={error.length > 0 ? error : ''}
                                clearMessage={() => {
                                    setError('');
                                }}
                            ></SnackbarMessage>
                        </Box>
                        {successMessage.length > 0 ? (
                            <Box>
                                <SnackbarMessage
                                    serverity="success"
                                    message={successMessage}
                                    clearMessage={() => {
                                        setSuccessMessage('');
                                    }}
                                />
                            </Box>
                        ) : null}
                        {props.error && (
                            <Box>
                                <SnackbarMessage
                                    serverity="error"
                                    message={props.error}
                                    clearMessage={() => {
                                        dispatch(clearCoachError());
                                    }}
                                />
                            </Box>
                        )}
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'end', marginRight: '2rem' }}>
                    <Button
                        onClick={() => handleSubmit(description, image, phoneNumber, props.userId)}
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        sx={{ backgroundColor: '#E3F2FD', color: '#2196F3' }}
                    >
                        {strings.newsAndEvents.buttons.save}
                    </Button>
                </Box>
            </div>
        </div>
    );
};

function mapStateToProps(state: AppState) {
    return {
        userId: getUserId(state),
        languages: getLanguagesList(state),
        coach: getCoachList(state),
        error: getError(state),
    };
}

export default connect(mapStateToProps)(AboutMePage);
